// Wind-barb icons rendered as SVG and registered into the MapLibre
// style. Standard meteorological convention:
//
//   * Tail points TOWARD the direction wind is FROM.
//     (icon-rotate = windDirDeg.)
//   * Half-flag = 5 kt
//   * Full flag = 10 kt
//   * Pennant   = 50 kt
//
// Each speed bucket gets its own icon; the layer rotates the icon by
// the feature's windDirDeg. We render one icon per 5-kt bucket from
// 0 to 100+ kt so the wind-barb layer covers any realistic conditions
// without being an icon-image-per-feature.
//
// Bucket key: `wb-{kt}` where kt is 0, 5, 10, ..., 100. Calm (<=2 kt)
// shows a small open circle.
//
// Coordinate system: SVG uses y-down. The barb tail extends from the
// station origin (cx,cy) UPWARD along -y; flags are drawn on the right
// side of the tail (positive x), at the tail end first, working toward
// the origin. icon-rotate rotates the entire SVG clockwise from north,
// which is exactly the meteorological convention we want.

import type { Map as MlMap, ExpressionSpecification } from 'maplibre-gl';
import { ensureSvgImages, type SvgSymbol } from './iconRegistry';

/** SVG canvas dimensions. Barb origin at center; tail extends upward. */
const SIZE = 36;
const CX = SIZE / 2;
const CY = SIZE / 2 + 8;          // station origin slightly below center so tail fits
const TAIL_LEN = 18;
const FLAG_LEN = 7;
const FLAG_GAP = 3;               // gap between successive flags along the tail

const STROKE = '#0b0d0f';
const FILL_FULL = '#e5e7eb';      // light gray-white for half/full flags
const FILL_PENN = '#dc2626';      // red pennant for high winds (>=50kt) so it pops

/** Build SVG for a wind barb at speed `kt`. */
function svgBarb(kt: number): string {
  const calm = kt <= 2;
  if (calm) {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
        <circle cx="${CX}" cy="${CY}" r="3" fill="none" stroke="${STROKE}" stroke-width="1.6"/>
      </svg>
    `;
  }

  // Reduce speed into pennants(50), full flags(10), half flags(5).
  // Round to nearest 5 to pick an icon.
  let rem = Math.round(kt / 5) * 5;
  const pennants = Math.floor(rem / 50); rem -= pennants * 50;
  const fulls    = Math.floor(rem / 10); rem -= fulls * 10;
  const halves   = Math.floor(rem / 5);  // 0 or 1

  // Build the tail line.
  const tailEndY = CY - TAIL_LEN;
  let body = `<line x1="${CX}" y1="${CY}" x2="${CX}" y2="${tailEndY}" stroke="${STROKE}" stroke-width="2" stroke-linecap="round"/>`;

  // Walk down from the tail end placing flags. Position is the y of
  // the BASE of each flag (closest to the tail end).
  let pos = tailEndY;

  // Pennants (filled triangles, base on the tail)
  for (let i = 0; i < pennants; i++) {
    const y0 = pos;                // base attached to tail
    const y1 = pos + FLAG_LEN * 0.9; // pointed end further down
    body += `<polygon points="${CX},${y0} ${CX + FLAG_LEN},${y0} ${CX},${y1}" fill="${FILL_PENN}" stroke="${STROKE}" stroke-width="0.6"/>`;
    pos = y1 + FLAG_GAP;
  }

  // Full flags (filled rectangles slanted up-right)
  for (let i = 0; i < fulls; i++) {
    const y0 = pos;
    const y1 = pos + FLAG_GAP * 0.8;
    body += `<polygon points="${CX},${y0} ${CX + FLAG_LEN},${y0 - FLAG_GAP * 1.4} ${CX + FLAG_LEN * 0.7},${y1} ${CX},${y1}" fill="${FILL_FULL}" stroke="${STROKE}" stroke-width="0.6"/>`;
    pos = y1 + FLAG_GAP;
  }

  // Half flag (one half-length filled tick)
  for (let i = 0; i < halves; i++) {
    const y0 = pos;
    const y1 = pos + FLAG_GAP * 0.7;
    body += `<polygon points="${CX},${y0} ${CX + FLAG_LEN * 0.55},${y0 - FLAG_GAP * 1.0} ${CX + FLAG_LEN * 0.4},${y1} ${CX},${y1}" fill="${FILL_FULL}" stroke="${STROKE}" stroke-width="0.6"/>`;
    pos = y1 + FLAG_GAP;
  }

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      ${body}
    </svg>
  `;
}

/** Speed buckets we pre-render. 0 = calm. */
const BUCKETS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];

export function barbIconId(kt: number | null | undefined): string {
  if (kt == null || !Number.isFinite(kt) || kt <= 2) return 'wb-0';
  const cap = Math.min(100, Math.max(0, Math.round(kt / 5) * 5));
  return `wb-${cap}`;
}

/** A few representative wind-barb SVGs for the legend. */
export const WIND_LEGEND_SVGS = {
  '5':  svgBarb(5),
  '10': svgBarb(10),
  '20': svgBarb(20),
  '50': svgBarb(50),
};

export function ensureWindBarbIcons(map: MlMap): Promise<void> {
  // Build the symbol array lazily from BUCKETS so we keep the
  // bucket-driven generation contained in this module while still
  // using the shared registry.
  const symbols: SvgSymbol[] = BUCKETS.map((kt) => ({
    id: `wb-${kt}`,
    svg: svgBarb(kt),
  }));
  return ensureSvgImages(map, symbols, {
    defaultSize: SIZE,
    logTag: '[windBarbs]',
  });
}

/** Layer-style expression that picks the right barb icon for a feature
 *  based on its windKt property. Step-binned to match BUCKETS. */
export const WIND_BARB_ICON_EXPRESSION: ExpressionSpecification = [
  'concat',
  'wb-',
  // Round windKt to nearest 5, clamp to [0, 100].
  [
    'to-string',
    [
      'min',
      100,
      [
        'max',
        0,
        [
          '*',
          5,
          ['round', ['/', ['coalesce', ['to-number', ['get', 'windKt']], 0], 5]],
        ],
      ],
    ],
  ],
] as unknown as ExpressionSpecification;
